import { useState, type ReactNode } from 'react';
import { Check, Copy, ExternalLink, Lock, Shield } from 'lucide-react';

import type { SecureFormField } from '../../lib/api';

export const FIELD_INPUT_CLASS =
  'w-full rounded-md border border-zt-border bg-zt-bg px-3 py-2.5 text-sm text-slate-100 placeholder:text-zt-muted/70 transition focus:border-zt-primary focus:outline-none focus:ring-1 focus:ring-zt-primary/40';

export function formatSsnInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function maskSsnDisplay(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return '•••-••-••••';
  return `•••-••-${digits.slice(-4)}`;
}

export function inputTypeFor(field: SecureFormField): string {
  switch (field.type) {
    case 'ssn':
    case 'ein':
      return 'password';
    case 'email':
      return 'email';
    case 'tel':
      return 'tel';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}

export function SecureFormCard({
  title,
  badge = 'Encrypted — not sent to AI',
  children,
  className = '',
}: {
  title: string;
  badge?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`w-full max-w-lg overflow-hidden rounded-xl border border-zt-primary/35 bg-gradient-to-b from-emerald-950/35 to-zt-surface/80 shadow-[0_0_0_1px_rgba(16,185,129,0.06)] ${className}`}
    >
      <div className="flex min-w-0 items-center gap-2.5 border-b border-zt-primary/20 bg-emerald-950/30 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zt-primary/30 bg-zt-bg/60">
          <Shield className="text-zt-primary" size={15} />
        </div>
        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-emerald-50" title={title}>
          {title}
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-zt-primary/25 bg-zt-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zt-accent">
          <Lock size={10} /> {badge}
        </span>
      </div>
      <div className="space-y-3.5 px-4 py-4">{children}</div>
    </div>
  );
}

export function SecureFormShareStrip({
  shareUrl,
  recipientName,
  expiresAt,
  purpose,
}: {
  shareUrl: string;
  recipientName?: string;
  expiresAt?: string;
  purpose?: string;
}) {
  const [copied, setCopied] = useState(false);
  const who = recipientName?.trim() || 'this recipient';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rounded-lg border border-zt-primary/25 bg-zt-bg/50 px-3 py-3">
      <p className="text-sm font-medium text-emerald-100">Send only to {who}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-zt-muted">
        One form per person. Do not share with other members.
        {purpose ? ` Purpose: ${purpose}.` : ''}
        {expiresAt ? ` Expires ${new Date(expiresAt).toLocaleString()}.` : ''}
      </p>
      <div className="mt-2.5 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
        <input
          readOnly
          value={shareUrl}
          className="min-w-0 flex-1 truncate rounded-md border border-zt-border bg-zt-elevated px-3 py-2 font-mono text-[11px] text-emerald-100/90"
          onFocus={(e) => e.target.select()}
          aria-label="Shareable form link"
        />
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-zt-primary px-3 py-2 text-xs font-medium text-zt-bg hover:bg-zt-accent"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <a
          href={shareUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-zt-primary/35 px-3 py-2 text-xs font-medium text-emerald-100 hover:bg-zt-primary/10"
        >
          <ExternalLink size={13} /> Open
        </a>
      </div>
    </div>
  );
}

export function SecureFormFieldLabel({
  label,
  required,
  saved,
}: {
  label: string;
  required?: boolean;
  saved?: boolean;
}) {
  return (
    <span className="mb-1.5 block text-xs font-medium leading-snug text-slate-300">
      {label}
      {required !== false && <span className="text-zt-danger"> *</span>}
      {saved && (
        <span className="ml-2 inline-flex items-center gap-0.5 font-normal text-zt-primary">
          <Check size={11} /> saved
        </span>
      )}
    </span>
  );
}

export function SecureFormFieldControl({
  field,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
}: {
  field: SecureFormField;
  value: string;
  onChange: (raw: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
}) {
  const options = field.options;
  if (field.type === 'select' && options && options.length > 0) {
    return (
      <select
        className={FIELD_INPUT_CLASS}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type={inputTypeFor(field)}
      autoComplete="off"
      disabled={disabled}
      inputMode={field.type === 'ssn' || field.type === 'ein' ? 'numeric' : undefined}
      className={`${FIELD_INPUT_CLASS} disabled:opacity-60`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={field.type === 'ssn' ? '•••-••-••••' : undefined}
    />
  );
}
